/**
 * Les sprites du skin 8 bits, en cartes de caractères — un caractère, un pixel
 * logique. C'est la forme donnée par le handoff (`sprite/steve-sprite.md`), et
 * c'est aussi la plus lisible pour retoucher un pixel à la main.
 *
 * Aucun fichier image : les cartes sont dessinées dans un canvas et affichées
 * en `image-rendering: pixelated`. Zéro requête, net à tous les zooms, et le
 * même dessin sert au sprite de la page et à celui du mini-jeu.
 */

/** Palette du sprite de Steve (handoff § Sprite). */
export const SPRITE_PALETTE: Readonly<Record<string, string>> = {
  K: '#f4f4f8',
  O: '#f97316',
  S: '#d9560c',
  D: '#c2410c',
  W: '#ffffff',
  P: '#0b0b16',
};

/**
 * Steve, 34 × 20 — relevé cellule par cellule depuis `steve-sprite.json`
 * (handoff 12), jamais redessiné à l'œil.
 *
 * Deux choses portent le dessin, et une retouche à la main les casserait :
 *
 * **Le contour `K` est calculé, pas dessiné.** Toute cellule vide touchant un
 * pixel plein par un de ses quatre côtés devient contour, puis les poches d'une
 * seule cellule sont rebouchées. C'est ce qui donne une silhouette régulière
 * jusque dans le creux sous l'aisselle. Qui change la forme doit relancer ce
 * calcul, pas rattraper le contour cellule par cellule.
 *
 * **Le pédoncule de la queue.** Elle ne rejoint le corps qu'aux lignes 7-8 et
 * reste séparée par le contour aux lignes 5-6 et 9-10 : c'est ce détroit qui la
 * fait lire comme une queue et non comme un prolongement du ventre.
 *
 * Le contour ne doit jamais valoir la couleur du fond : sur les panneaux
 * sombres du skin, le crème fonctionne ; sur un fond clair, il faudrait passer
 * le contour à `#0b0b16`.
 */
export const STEVE_SPRITE: readonly string[] = [
  '..................................',
  '..............KKKKKKKK............',
  '............KKOOOOOOOOKK..........',
  '..KK......KKOOOOOOOOOOOOKK........',
  '.KOOK....KOOOOOOOOOOOOOOOOK.......',
  '.KOOOKK.KOOOOOOOOOOOOWWWWOOK......',
  '.KOOOOOKOOOOOOOOOOOOOWPPWOOOK.....',
  '.KOOOOOOOOOOOOOOOOOOOWPPWOOOK.....',
  '.KOOOOOOOOOOOOOOOOOOOWWWWOSSK.....',
  '.KOOOOOKOOOOOOOOOOOOOOOOOSSSK.....',
  '.KOOOKK.KOOOOOOOOOOOOOOOSSSK......',
  '.KOOK....KOOOOOOOOOOOOOOSSK.......',
  '..KK......KKOOOOOOOOSSSOOK........',
  '..........KDDKOOOOOSSSKKOOK.......',
  '.........KDDK.KOOKKOOKK.KOOK......',
  '..........KK..KOOKKOOK...KK.......',
  '..............KOOKKOOK............',
  '..............KOOKKOOKK...........',
  '.............KDDDDKDDDDK..........',
  '..............KKKK.KKKK...........',
];

/**
 * Le poisson sans jambes du mini-jeu, 11 × 7. Même silhouette que Steve, mais
 * sans bras ni jambes : c'est tout le drame de ces poissons-là.
 */
export const FOE_SPRITE: readonly string[] = [
  '...KKKKK...',
  '.KKOOOOOKK.',
  'KKOOOOWWOOK',
  'KOOOOOWPOOK',
  'KKOOOOWWOOK',
  '.KKOOOOOKK.',
  '...KKKKK...',
];

/** La bulle crachée par Steve, 3 × 3. */
export const BUBBLE_SPRITE: readonly string[] = ['.K.', 'KWK', '.K.'];

/** L'hameçon que les poissons laissent tomber, 3 × 5. */
export const HOOK_SPRITE: readonly string[] = ['.K.', '.K.', '.K.', 'K.K', '.KK'];

export interface Sprite {
  readonly rows: readonly string[];
  readonly width: number;
  readonly height: number;
}

/** Mesure une carte : la largeur est celle de la ligne la plus longue. */
export function measure(rows: readonly string[]): Sprite {
  return {
    rows,
    width: rows.reduce((max, row) => Math.max(max, row.length), 0),
    height: rows.length,
  };
}

export const STEVE = measure(STEVE_SPRITE);
export const FOE = measure(FOE_SPRITE);
export const BUBBLE = measure(BUBBLE_SPRITE);
export const HOOK = measure(HOOK_SPRITE);

/** Le sous-ensemble du contexte 2D dont le dessin a besoin (donc simulable). */
export interface PixelContext {
  fillStyle: string | CanvasGradient | CanvasPattern;
  fillRect: (x: number, y: number, w: number, h: number) => void;
}

/**
 * Dessine une carte à partir du coin haut-gauche, un caractère par pixel
 * logique, avec une palette éventuellement remplacée (le mini-jeu recolore les
 * rangées de poissons sans toucher aux cartes).
 */
export function drawSprite(
  ctx: PixelContext,
  sprite: Sprite,
  x: number,
  y: number,
  scale = 1,
  palette: Readonly<Record<string, string>> = SPRITE_PALETTE,
): void {
  for (const [row, line] of sprite.rows.entries()) {
    for (const [column, char] of [...line].entries()) {
      const color = palette[char];
      if (color === undefined) continue;
      ctx.fillStyle = color;
      ctx.fillRect(x + column * scale, y + row * scale, scale, scale);
    }
  }
}

/** Nombre de pixels réellement peints — sert aux tests et au dimensionnement. */
export function countPixels(sprite: Sprite): number {
  return sprite.rows.reduce(
    (total, row) => total + [...row].filter((char) => char !== '.').length,
    0,
  );
}
